import {
  cardStyle,
  Chips,
  column,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  GridTableApi,
  multiFilter,
  MultiSelectField,
  RowStyles,
  selectColumn,
  simpleHeader,
  singleFilter,
  Tag,
  useComputed,
  useFilter,
  useSnackbar,
} from "@homebound/beam";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { SearchBox, useNavigationCheck } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { useGlobalOptionTagsFilter } from "src/components/autoPopulateFilters/useGlobalOptionTagsFilter";
import { LocationsSelectField_LocationFragment } from "src/components/autoPopulateSelects/LocationsSelectField.generated";
import { useGlobalOptionTypes } from "src/components/autoPopulateSelects/useGlobalOptionTypes";
import { NextStepButton } from "src/components/stepper/useStepperWizard/NextStepButton";
import { Named_ReadyPlanLocation_Fragment, NamedFragment } from "src/fragments/commonFragments.generated";
import { GlobalOptionsFilter, GlobalOptionStatus, Maybe } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { TableActions } from "src/routes/layout/TableActions";
import { useSavePlanPackageDetailsMutation } from "src/routes/libraries/plan-package/stepper/1.PlanDetailsStep.generated";
import {
  ChooseOptionsStepBody_GlobalOptionFragment,
  ChooseOptionsStepBody_GlobalOptionsQuery,
  ChooseOptionsStepBody_PlanPackageQuery,
  ChooseOptionsStepBody_ReadyPlanOptionFragment,
  useChooseOptionsStepBody_GlobalOptionsQuery,
  useChooseOptionsStepBody_PlanPackageQuery,
} from "src/routes/libraries/plan-package/stepper/components/ChooseOptionsStepBody.generated";
import { PlanPackageNextStepButton } from "src/routes/libraries/plan-package/stepper/components/PlanPackageNextStepButton";
import { queryResult } from "src/utils";

type ChooseOptionsStepBodyProps = {
  planId: string;
  versionId: string;
  returnUrl?: string;
  /** Function to set the step as dirty or not dirty */
  setStepDirty: (dirty: boolean) => void;
  tableApi: GridTableApi<ChooseOptionsRow>;
};

export function ChooseOptionsStepBody(props: ChooseOptionsStepBodyProps) {
  const { planId, versionId, setStepDirty, returnUrl, tableApi } = props;
  const { triggerNotice } = useSnackbar();

  // Get the global option types to use in our filter
  const gots = useGlobalOptionTypes({
    isElevation: false,
    // This component is shared with OG RP stepper, which will not provide a versionId
    version: [2],
  });
  // This sublist of option types is used to limit the filter options, and the global options to query for when no filter is set
  const optionTypes = useMemo(() => gots?.filter((got) => got.globalOptionsCount > 0) ?? [], [gots]);

  const tagsFilter = useGlobalOptionTagsFilter();
  const ppQuery = useChooseOptionsStepBody_PlanPackageQuery({
    variables: {
      planPackageId: planId,
      rpavId: versionId,
    },
    // No point on running the query if option types have not been loaded yet
    skip: optionTypes.isEmpty,
  });
  const rplLocations = useMemo(() => ppQuery.data?.planPackage.roomLocations ?? [], [ppQuery.data]);
  const dpIds = ppQuery.data?.planPackage.designPackages.map((dp) => dp.id) ?? [];

  // Build the filter
  const filterDefs: FilterDefs<ChooseOptionsFilter> = useMemo(
    () => ({
      type: multiFilter({
        label: "Type",
        options: optionTypes,
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      locations: multiFilter({
        label: "Location",
        options: rplLocations,
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ id }) => id,
      }),
      tags: tagsFilter,
      sortBy: singleFilter({
        label: "Sort By",
        options: [
          { name: "Recently Edited", value: "updatedAt" as const },
          { name: "Recently Created", value: "createdAt" as const },
          { name: "Option Code", value: "code" as const },
        ],
        nothingSelectedText: "Recently Used First",
        getOptionLabel: ({ name }) => name,
        getOptionValue: ({ value }) => value,
      }),
    }),
    [optionTypes, rplLocations, tagsFilter],
  );
  const { filter, setFilter } = useFilter({ filterDefs });

  const goQuery = useChooseOptionsStepBody_GlobalOptionsQuery({
    variables: {
      filter: {
        ...mapToFilter(filter, optionTypes),
        status: [GlobalOptionStatus.Active],
        readyPlan: [null, ...dpIds],
      },
    },
    // No point on running the query if option types have not been loaded yet
    skip: optionTypes.isEmpty || ppQuery.loading,
  });
  // setRplLocations(query.data?.planPackage.roomLocations ?? emptyRplLocation);
  const [savePP] = useSavePlanPackageDetailsMutation();
  const disabled = disableBasedOnPotentialOperation(ppQuery.data?.planPackage.canEdit);

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const selectedRows = useComputed(() => tableApi.getSelectedRows("globalOption"), [tableApi]);

  // get from query, but also set locally
  const [rows, setRows] = useState<GridDataRow<ChooseOptionsRow>[]>([]);
  useEffect(() => {
    if (ppQuery.data && goQuery.data) setRows(createRows(ppQuery.data, goQuery.data, filter.sortBy ?? "lastUsedAt"));
  }, [ppQuery.data, goQuery.data, filter.sortBy]);

  const existingRposByKey = useMemo(
    () =>
      new Map<string, ChooseOptionsStepBody_ReadyPlanOptionFragment>(
        ppQuery.data?.planPackage.options.map((rpo) => [keyRpo(rpo), rpo]),
      ),
    [ppQuery.data],
  );

  const columns = useMemo(
    () => createColumns(rplLocations, rows, setRows, tableApi, existingRposByKey, disabled),
    [rplLocations, rows, setRows, tableApi, existingRposByKey, disabled],
  );

  const mapSelectionToSaveRpoInputs = useCallback(() => {
    const desiredRposByKey = new Map<string, { globalOptionId: string; readyPlanLocationId: string | undefined }>(
      selectedRows.flatMap((row) =>
        row.data.locations.map((loc) => [
          keyRpo(row.data.id, loc),
          { globalOptionId: row.data.id, readyPlanLocationId: loc },
        ]),
      ),
    );

    // create RPOs that are not in the existing RPOs
    const toCreate = [...desiredRposByKey.entries()]
      .filter(([key]) => !existingRposByKey.has(key))
      .map(([_, value]) => value);

    // enable/disable RPOs that are in the existing RPOs but not in the desired RPOs
    const toEnableDisable =
      [...existingRposByKey.values()]
        // we first filter only for the relevant option types, to prevent disabling options not displayed on this step
        .filter((rpo) => optionTypes.some((goType) => goType.id === rpo.type.id))
        // then we filter for the ones where there desired status does not match there current active state
        .filter((rpo) => desiredRposByKey.has(keyRpo(rpo)) !== rpo.active)
        // and toggle active for the existing options
        .map((rpo) => ({ id: rpo.id, active: !rpo.active })) ?? [];

    return [...toCreate, ...toEnableDisable];
  }, [existingRposByKey, optionTypes, selectedRows]);

  const onSave = useCallback(async () => {
    const saveRpoInputs = mapSelectionToSaveRpoInputs();
    if (saveRpoInputs.nonEmpty) {
      const response = await savePP({ variables: { input: { id: planId, options: saveRpoInputs } } });
      triggerNotice({ message: `Your Options have been updated!` });
      return response;
    }
  }, [mapSelectionToSaveRpoInputs, planId, savePP, triggerNotice]);

  const onClickSave = useCallback(async () => {
    await onSave();
  }, [onSave]);

  const saveRpoInputs = mapSelectionToSaveRpoInputs();
  const changeCount = saveRpoInputs.length;
  useEffect(() => {
    setStepDirty(changeCount > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeCount]);

  // if there are no active locations when user selects a row
  // activate the 'None' location
  useEffect(() => {
    selectedRows.forEach((row) => {
      if (row.data.locations.isEmpty) {
        row.data.locations.push(undefined);
        setRows([...rows]);
      }
    });
  }, [selectedRows, rows]);
  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => changeCount > 0, [changeCount]);

  return (
    <>
      {queryResult(goQuery, () => (
        <div css={Css.pt3.pb0.bgGray100.df.fdc.fg1.$}>
          <TableActions>
            <div css={Css.df.gap1.jcfe.$}>
              <Filters filter={filter} filterDefs={filterDefs} onChange={setFilter} />
            </div>
            <SearchBox onSearch={setSearchFilter} clearable updateQueryString={false} />
          </TableActions>

          <div css={Css.fg1.pb1.$}>
            <GridTable
              api={tableApi}
              rows={rows}
              columns={columns}
              filter={searchFilter}
              style={tableStyles}
              rowStyles={rowStyles}
              as="virtual"
            />
          </div>
        </div>
      ))}
      {versionId ? (
        <PlanPackageNextStepButton planId={planId} versionId={versionId} onSave={onSave} />
      ) : (
        <NextStepButton
          label="Continue"
          onClick={onClickSave}
          onCloseReturnUrl={returnUrl}
          exitButton={{ variant: "secondary", label: "Save & Exit", onClick: onClickSave }}
        />
      )}
    </>
  );
}

type ChooseOptionsFilter = GlobalOptionsFilter & {
  sortBy: undefined | "lastUsedAt" | "createdAt" | "updatedAt" | "code";
};

function createColumns(
  rplLocations: Named_ReadyPlanLocation_Fragment[],
  rows: GridDataRow<ChooseOptionsRow>[],
  setRows: React.Dispatch<React.SetStateAction<GridDataRow<ChooseOptionsRow>[]>>,
  api: GridTableApi<ChooseOptionsRow>,
  existingRposByKey: Map<string, ChooseOptionsStepBody_ReadyPlanOptionFragment>,
  disabled?: ReactNode,
): GridColumn<ChooseOptionsRow>[] {
  return [
    ...(disabled
      ? [
          column<ChooseOptionsRow>({
            header: "",
            globalOption: ({ id, locations }) => ({
              content: (
                <input
                  type="checkbox"
                  disabled
                  checked={locations.some((loc) => {
                    if (id === "go:2240")
                      console.log({ loc, id, key: keyRpo(id, loc), existing: existingRposByKey.get(keyRpo(id, loc)) });
                    return (
                      !existingRposByKey.has(keyRpo(id, loc)) ||
                      existingRposByKey.get(keyRpo(id, loc))?.active !== false
                    );
                  })}
                />
              ),
              value: id,
            }),
            w: "45px",
          }),
        ]
      : [
          selectColumn<ChooseOptionsRow>({
            // Prevent users from selecting/deselecting all rows for plan options
            header: "",
          }),
        ]),
    column<ChooseOptionsRow>({
      header: "Option Code",
      globalOption: ({ code }) => ({ content: () => code, css: Css.xs.$ }),
      w: "150px",
    }),
    column<ChooseOptionsRow>({
      header: "Name",
      globalOption: ({ displayName }) => ({ content: displayName, css: Css.sm.$ }),
      w: 3,
    }),
    column<ChooseOptionsRow>({
      header: "Type",
      globalOption: ({ type }) => ({ content: <Tag text={type.name} />, css: Css.sm.$, value: type.name }),
    }),
    column<ChooseOptionsRow>({
      header: "Location",
      globalOption: (data, opts) => (
        <MultiSelectField<LocationsSelectField_LocationFragment, string | undefined>
          disabled={
            disabled || (!data.type.allowsLocations && `Options of type ${data.type.name} do not allow locations`)
          }
          disabledOptions={data.disabledLocations}
          label="Locations"
          options={[{ name: "None", id: undefined } as any, ...rplLocations]}
          values={data.locations}
          getOptionMenuLabel={(loc) => {
            if (!loc.id) return loc.name;

            return (
              <ArchivedTag
                active={
                  !existingRposByKey.has(keyRpo(data.id, loc.id)) ||
                  existingRposByKey.get(keyRpo(data.id, loc.id))?.active !== false
                }
              >
                <span>{loc.name} </span>
              </ArchivedTag>
            );
          }} // add archived tag if rpo exists for this location but is inactive
          getOptionValue={(loc) => loc.id}
          onSelect={(selectedLocations) => {
            // set locations in the row
            const locations = selectedLocations.map((id) => id);

            // get parent and child that matches this row
            // so we can set data in the rows state
            const rowIndex = rows.findIndex((r) => r.id === opts.row.id);
            if (rowIndex !== undefined && rows[rowIndex]) {
              // need to keep existing, but archived, locations
              rows[rowIndex].data = { ...data, locations };
              setRows([...rows]);

              // set selected if there are locations selected (including "None" option)
              // deselected if zero are selected
              api.selectRow(opts.row.id, selectedLocations.length > 0);
            }
          }}
        />
      ),
    }),
    column<ChooseOptionsRow>({
      header: "Tag",
      globalOption: (data) => ({
        content: () => (data.tags.length ? <Chips values={data.tags.map((t) => t.name)} wrap compact /> : undefined),
        value: data.tags.map((t) => `${t.id} ${t.name}`).join(" "),
      }),
    }),
  ];
}

/** Create rows of "GO + Location" pairs. */
function createRows(
  ppQuery: ChooseOptionsStepBody_PlanPackageQuery | undefined,
  goQuery: ChooseOptionsStepBody_GlobalOptionsQuery,
  sortBy: "lastUsedAt" | "createdAt" | "updatedAt" | "code",
): GridDataRow<ChooseOptionsRow>[] {
  // Create a set of the ReadyPlan's already-selected
  // In theory we could `keyBy` instead of `groupBy`, but there is legacy RPOs that are duplicated
  const existingRpos = ppQuery?.planPackage.options.groupBy((rpo) => rpo.globalOption.id) ?? {};
  // Group by option type
  const [initiallySelected, others] = goQuery?.globalOptions
    .map(
      (go) =>
        ({
          kind: "globalOption" as const,
          id: go.id,
          data: {
            ...go,
            readyPlanOptions: existingRpos[go.id],
            locations: existingRpos[go.id]?.filter((rpo) => rpo.active).map((rpo) => rpo.readyPlanLocation?.id) ?? [],
            disabledLocations:
              existingRpos[go.id]
                ?.filter((rpo) => rpo.active && !rpo.canDelete.allowed)
                .map((rpo) => ({
                  value: rpo.readyPlanLocation?.id,
                  reason: rpo.canDelete.disabledReasons.map((r) => r.message).join(", "),
                })) ?? [],
          },
          initSelected: !!existingRpos[go.id]?.some((rpo) => rpo.active),
        }) as GridDataRow<GlobalOptionRow>,
    )
    .partition((c) => c.initSelected);

  function sortOptions(options: GridDataRow<GlobalOptionRow>[]): GridDataRow<GlobalOptionRow>[] {
    // For lastUsedAt, which may be null, use a date before HB was founded so that they sort to the end
    const gosSorted = options.sortBy((c) => c.data[sortBy] ?? new Date(2000, 0, 1));
    if (sortBy === "code") return gosSorted;
    // For the dates, we want the earliest first, which is the reverse of the standard sort
    return gosSorted.reverse();
  }

  return [simpleHeader, ...sortOptions(initiallySelected), ...sortOptions(others)];
}

function mapToFilter(filterWithSort: ChooseOptionsFilter, globalOptionTypes: NamedFragment[]): GlobalOptionsFilter {
  const { sortBy, ...filter } = filterWithSort;
  // If `type` filter is empty, return only the globalOptionTypes that we want to show (i.e. Elevation may or may not be included)
  const type = filter.type && filter.type.length > 0 ? filter.type.compact() : globalOptionTypes.map((got) => got.id);
  return { ...filter, type };
}

const tableStyles = {
  ...cardStyle,
  cellCss: {
    ...cardStyle.cellCss,
    ...Css.aic.pPx(12).$,
  },
  presentationSettings: {
    borderless: false,
    typeScale: "xs" as const,
    wrap: false,
  },
};

const rowStyles: RowStyles<ChooseOptionsRow> = {
  header: {
    rowCss: Css.bgGray100.bn.important.$,
    cellCss: Css.bgGray100.bn.important.$,
  },
  globalOption: {
    rowCss: Css.bgWhite.bn.boxShadow("0px 4px 8px 0px #35353514").mt1.borderRadius("8px").hPx(44).$,
    cellCss: Css.bgWhite.$,
  },
};

// Helper method for comparing desired and existing RPOs
function keyRpo(globalOptionId: string, rplId: string | undefined): string;
function keyRpo(rpo: ChooseOptionsStepBody_ReadyPlanOptionFragment): string;
function keyRpo(
  rpoOrGlobalOptionId: string | ChooseOptionsStepBody_ReadyPlanOptionFragment,
  rplId?: string | undefined,
): string {
  if (typeof rpoOrGlobalOptionId === "string") return `${rpoOrGlobalOptionId}-${rplId}`;
  return `${rpoOrGlobalOptionId.globalOption.id}-${rpoOrGlobalOptionId.readyPlanLocation?.id}`;
}

type HeaderRow = {
  kind: "header";
  id: string;
  data: undefined;
};
export type GlobalOptionRow = {
  kind: "globalOption";
  id: string | null;
  data: ChooseOptionsStepBody_GlobalOptionFragment & {
    // Include the existing RPO, even if it's inactive
    readyPlanOptions: Maybe<ChooseOptionsStepBody_ReadyPlanOptionFragment>[];
    locations: Array<string | undefined>;
    disabledLocations: {
      value: string;
      reason: string;
    }[];
  };
  initSelected: boolean;
};

export type ChooseOptionsRow = HeaderRow | GlobalOptionRow;
